import React, { useState, useEffect, useCallback } from 'react';
import { FaStop, FaHourglassHalf, FaCheckSquare, FaCog } from 'react-icons/fa';
import '../styles/animations.css';

interface TimerControlsProps {
  onStartSession: (duration: number, isWordGoal: boolean, goalTarget?: number) => void;
  onStartNewSession?: (duration: number, isWordGoal: boolean, goalTarget?: number) => void;
  onResetSession: () => void;
  isSessionActive: boolean;
  sessionDuration: number;
  timeRemaining: number;
  wordCount: number;
  wordGoal: number;
  isWordGoalMode: boolean;
}

const TimerControls: React.FC<TimerControlsProps> = ({
  onStartSession,
  onStartNewSession,
  onResetSession,
  isSessionActive,
  sessionDuration,
  timeRemaining,
  wordCount,
  wordGoal,
  isWordGoalMode
}) => {
  const [selectedDuration, setSelectedDuration] = useState<number>(5);
  const [isWordGoal, setIsWordGoal] = useState<boolean>(false);
  
  // Set different default word goal for mobile vs desktop
  const isMobile = window.innerWidth <= 768;
  const [tempWordGoal, setTempWordGoal] = useState<number>(isMobile ? 250 : 500);
  
  const [showCustomDuration, setShowCustomDuration] = useState<boolean>(false);
  const [customMinutes, setCustomMinutes] = useState<number>(15);
  const [customSeconds, setCustomSeconds] = useState<number>(0);
  
  // Helper function to return focus to the text area
  const returnFocusToTextArea = useCallback(() => {
    if (isSessionActive) {
      setTimeout(() => {
        const textArea = document.querySelector('.editor-container textarea') as HTMLTextAreaElement;
        if (textArea) {
          textArea.focus();
        }
      }, 10);
    }
  }, [isSessionActive]);
  
  // Format time for display (minutes:seconds)
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };
  
  // Calculate progress percentage
  const calculateProgress = (): number => {
    if (isWordGoalMode) {
      return Math.min(100, (wordCount / wordGoal) * 100);
    } else if (sessionDuration > 0) {
      return 100 - ((timeRemaining / (sessionDuration * 60)) * 100);
    }
    return 0;
  };
  
  // Handle start session with focus management
  const handleStart = () => {
    if (showCustomDuration) {
      // Convert custom time to minutes
      const totalMinutes = customMinutes + (customSeconds / 60);
      onStartSession(totalMinutes, isWordGoal, isWordGoal ? tempWordGoal : undefined);
    } else {
      onStartSession(selectedDuration, isWordGoal, isWordGoal ? tempWordGoal : undefined);
    }
    
    // Return focus to text area after session starts
    returnFocusToTextArea();
  };
  
  // Duration options
  const durationOptions = [
    { value: 3, label: '3 min' },
    { value: 5, label: '5 min' },
    { value: 10, label: '10 min' },
  ];
  
  // Handle custom minutes input with focus management
  const handleCustomMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 0 && value <= 60) {
      setCustomMinutes(value);
    }
  };
  
  // Handle custom seconds input with focus management
  const handleCustomSecondsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 0 && value < 60) {
      setCustomSeconds(value);
    }
  };
  
  // Handle duration button click with focus management
  const handleDurationButtonClick = (duration: number) => {
    setSelectedDuration(duration);
    setShowCustomDuration(false);
    returnFocusToTextArea();
  };
  
  // Handle custom duration toggle with focus management 
  const handleCustomDurationToggle = () => {
    setShowCustomDuration(!showCustomDuration);
    returnFocusToTextArea();
  };
  
  // Handle reset session with focus management
  const handleResetSession = () => {
    onResetSession();
    returnFocusToTextArea();
  };
  
  // Set focus to text area when a session becomes active
  useEffect(() => {
    if (isSessionActive) {
      returnFocusToTextArea();
    }
  }, [isSessionActive, returnFocusToTextArea]);
  
  return (
    <div className="flex flex-col allow-focus">
      {isSessionActive ? (
        <div className="space-y-5 animate-fade-in">
          {/* Active Session Display */}
          <div className="relative h-5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div 
              className={`absolute top-0 left-0 h-full transition-all duration-1000 ease-linear
                ${isWordGoalMode 
                  ? 'bg-primary' 
                  : 'bg-gradient-to-r from-primary to-green-400'
                }`}
              style={{ width: `${calculateProgress()}%` }}
            ></div>
          </div>
          
          <div className="flex justify-between items-center">
            <div className="text-xl font-mono text-primary font-semibold">
              {isWordGoalMode 
                ? `${wordCount}/${wordGoal} words` 
                : formatTime(timeRemaining)
              }
            </div>
            
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {isWordGoalMode 
                ? `${Math.round(calculateProgress())}% complete` 
                : `${Math.round(calculateProgress())}% elapsed`
              }
            </div>
          </div>
          
          <button
            onClick={handleResetSession}
            className="w-full flex items-center justify-center bg-red-500 hover:bg-red-600
              text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200
              focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50
              hover-lift button-press end-session-btn"
          >
            <FaStop className="mr-2" />
            <span>End Session</span>
          </button>
        </div>
      ) : (
        <div className="space-y-6 animate-fade-in">
          {/* Session Configuration */}
          <div className="space-y-4">
            {/* Session Type - Modified Layout */}
            <div className="flex flex-col items-center mb-2 session-type-toggle">
              <label className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-2">
                Session Type
              </label>
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => setIsWordGoal(false)}
                  className={`py-2 px-4 rounded-lg text-sm font-medium transition-colors duration-200
                    ${!isWordGoal 
                      ? 'bg-primary text-white' 
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }
                    focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50
                    button-press`}
                >
                  Time
                </button>
                <button
                  onClick={() => setIsWordGoal(true)}
                  className={`py-2 px-4 rounded-lg text-sm font-medium transition-colors duration-200
                    ${isWordGoal 
                      ? 'bg-primary text-white' 
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }
                    focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50
                    button-press`}
                >
                  Word Count
                </button>
              </div>
            </div>
            
            {isWordGoal ? (
              <div className="space-y-3 animate-fade-in">
                <div className="flex justify-between items-center">
                  <label className="block text-base font-medium text-gray-700 dark:text-gray-300">
                    Word Goal
                  </label>
                </div>
                
                <div className="word-goal-input">
                  <input
                    type="number"
                    min="1"
                    max="10000"
                    value={tempWordGoal}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (!isNaN(value) && value > 0) {
                        setTempWordGoal(value);
                      }
                    }}
                    onBlur={returnFocusToTextArea}
                    className="block w-full px-3 py-2 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 
                      border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 
                      focus:ring-primary transition-colors"
                  />
                  <span className="text-gray-500 dark:text-gray-400">words</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Session will notify you when you reach this goal
                </p>
              </div>
            ) : (
              <div className="space-y-3 animate-fade-in">
                {/* Session Duration Label - Centered */}
                <div className="text-center">
                  <label className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Session Duration
                  </label>
                </div>
                
                {/* Preset Duration Buttons */}
                <div className="flex flex-wrap gap-3 justify-between duration-buttons">
                  {durationOptions.map(option => (
                    <button
                      key={option.value}
                      onClick={() => handleDurationButtonClick(option.value)}
                      className={`py-3 px-4 rounded-lg text-sm font-medium transition-colors duration-200 flex-1 min-w-[70px] text-center
                        ${selectedDuration === option.value && !showCustomDuration
                          ? 'bg-primary text-white selected'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }
                        focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50
                        button-press`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                {/* Custom Duration Button - Moved and Restyled */}
                <button 
                  onClick={handleCustomDurationToggle}
                  className={`w-full flex items-center justify-center gap-2 text-sm py-3 px-4 mt-3 rounded-lg transition-colors duration-200
                    ${showCustomDuration 
                      ? 'bg-primary text-white' 
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }
                    focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50 button-press`}
                >
                  <FaCog className="w-4 h-4" /> 
                  <span>Custom Duration</span>
                </button>
                
                {showCustomDuration && (
                  <div className="mt-4 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 animate-fade-in">
                    <div className="flex gap-2 items-center">
                      <div className="flex-1">
                        <label htmlFor="custom-minutes" className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Minutes</label>
                        <input
                          type="number"
                          id="custom-minutes"
                          min="0"
                          max="60"
                          value={customMinutes}
                          onChange={handleCustomMinutesChange}
                          onBlur={returnFocusToTextArea}
                          className="block w-full px-3 py-2 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 
                            border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 
                            focus:ring-primary transition-colors"
                        />
                      </div>
                      <div className="flex-1">
                        <label htmlFor="custom-seconds" className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Seconds</label>
                        <input
                          type="number"
                          id="custom-seconds"
                          min="0"
                          max="59"
                          value={customSeconds}
                          onChange={handleCustomSecondsChange}
                          onBlur={returnFocusToTextArea}
                          className="block w-full px-3 py-2 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 
                            border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 
                            focus:ring-primary transition-colors"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 italic">
                      Set any duration from 1 second to 1 hour
                    </p>
                  </div>
                )}
                
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Session will end after the selected time
                </p>
              </div>
            )}
          </div>
          
          <button
            onClick={handleStart}
            className="w-full flex items-center justify-center bg-primary hover:bg-primary-dark
              text-white font-medium py-4 px-4 rounded-lg transition-colors duration-200
              focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50
              hover-lift button-press start-session-btn"
          >
            {isWordGoal ? (
              <>
                <FaCheckSquare className="mr-2" />
                <span>Start Word Goal Session</span>
              </>
            ) : (
              <>
                <FaHourglassHalf className="mr-2 animate-pulse" />
                <span>
                  {showCustomDuration 
                    ? `Start ${customMinutes}m ${customSeconds}s Session` 
                    : `Start ${selectedDuration}-min Session`}
                </span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default TimerControls; 